import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, ShieldCheck, Filter } from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../store/useAuthStore.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import type { User } from '../../../shared/types/index.js';
import { UserSearchBar } from './user-management/UserSearchBar.js';
import { UserStatsGrid } from './user-management/UserStatsGrid.js';
import { UserTable } from './user-management/UserTable.js';
import { UserPagination } from './user-management/UserPagination.js';
import { CreateUserModal } from './user-management/CreateUserModal.js';
import { EditUserModal } from './user-management/EditUserModal.js';
import { DeleteUserModal } from './user-management/DeleteUserModal.js';
import { PublisherVerificationModalAdmin } from './user-management/PublisherVerificationModalAdmin.js';

export const UserManagementView: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const { showToast } = useNotificationStore();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const limit = 20;

  // Modals / forms state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [verifyingPublisher, setVerifyingPublisher] = useState<User | null>(null);

  // Form input state (Create / Edit)
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState<'penulis' | 'penerbit' | 'organisasi' | 'admin'>('penulis');
  const [formOrg, setFormOrg] = useState('');
  const [formPubCode, setFormPubCode] = useState('');
  const [formVerificationStatus, setFormVerificationStatus] = useState<'verified' | 'rejected' | 'pending' | 'unverified'>('unverified');
  const [formVerificationNotes, setFormVerificationNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch users with search query and pagination
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['adminUsers', searchQuery, page],
    queryFn: () => api.getUsers(searchQuery, limit, page * limit),
    enabled: currentUser?.role === 'admin',
  });

  const usersList = data?.users || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  // Reset page when search query changes
  React.useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const pendingPublishersCount = useMemo(() => {
    return usersList.filter(
      (u: User) => (u.role === 'penerbit' || u.role === 'organisasi') && u.publisherVerificationStatus === 'pending'
    ).length;
  }, [usersList]);

  const filteredUsers = useMemo(() => {
    if (activeFilter === 'pending_publisher') {
      return usersList.filter(
        (u: User) => (u.role === 'penerbit' || u.role === 'organisasi') && u.publisherVerificationStatus === 'pending'
      );
    }
    if (activeFilter === 'publisher') {
      return usersList.filter((u: User) => u.role === 'penerbit');
    }
    if (activeFilter === 'organisasi') {
      return usersList.filter((u: User) => u.role === 'organisasi');
    }
    if (activeFilter === 'penulis') {
      return usersList.filter((u: User) => u.role === 'penulis');
    }
    if (activeFilter === 'admin') {
      return usersList.filter((u: User) => u.role === 'admin');
    }
    return usersList;
  }, [usersList, activeFilter]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (payload: any) => api.createUser(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      showToast('Pengguna Dibuat', 'Akun pengguna baru berhasil ditambahkan ke sistem.', 'success');
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setFormError(err.message || 'Gagal membuat pengguna baru');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => api.updateUserAdmin(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      showToast('Data Diperbarui', 'Data profil dan hak akses pengguna berhasil disimpan.', 'success');
      setEditingUser(null);
      resetForm();
    },
    onError: (err: any) => {
      setFormError(err.message || 'Gagal memperbarui data pengguna');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      showToast('Pengguna Dihapus', 'Akun pengguna telah dihapus dari sistem.', 'success');
      setDeletingUser(null);
    },
    onError: (err: any) => {
      showToast('Gagal Menghapus', err.message || 'Gagal menghapus pengguna', 'error');
    },
  });

  const verifyPublisherMutation = useMutation({
    mutationFn: ({
      id,
      status,
      notes,
    }: {
      id: string;
      status: 'verified' | 'rejected' | 'pending' | 'unverified';
      notes?: string;
    }) => api.verifyPublisherAdmin(id, status, notes),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['publishers'] });
      queryClient.invalidateQueries({ queryKey: ['stats'] });
      showToast('Status Verifikasi Diperbarui', res.message || 'Status verifikasi penerbit berhasil diubah.', 'success');
      setVerifyingPublisher(null);
    },
    onError: (err: any) => {
      alert(err.message || 'Gagal memperbarui status verifikasi penerbit');
    },
  });

  const handleOpenPublisherVerification = (usr: User) => {
    setVerifyingPublisher(usr);
  };

  const handlePublisherVerificationSubmit = (
    status: 'verified' | 'rejected' | 'pending' | 'unverified',
    notes?: string
  ) => {
    if (!verifyingPublisher) return;
    verifyPublisherMutation.mutate({
      id: verifyingPublisher.id,
      status,
      notes,
    });
  };

  const handleToggleVerifyPublisher = (usr: User) => {
    // Open verification detail modal
    setVerifyingPublisher(usr);
  };

  const resetForm = () => {
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('penulis');
    setFormOrg('');
    setFormPubCode('');
    setFormVerificationStatus('unverified');
    setFormVerificationNotes('');
    setFormError(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    const randomPass = Math.random().toString(36).substring(2, 10);
    setFormPassword(randomPass);
    setIsCreateOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    resetForm();
    setEditingUser(user);
    setFormName(user.name);
    setFormEmail(user.email);
    setFormRole(user.role);
    setFormOrg(user.organization || '');
    setFormPubCode(user.publisherCode || '');
    const currentStatus = user.publisherVerificationStatus || (user.isPublisherVerified ? 'verified' : 'unverified');
    setFormVerificationStatus(currentStatus as any);
    setFormVerificationNotes(user.publisherVerificationNotes || '');
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formName.trim() || !formEmail.trim() || !formPassword.trim()) {
      setFormError('Nama, Email, dan Kata Sandi wajib diisi.');
      return;
    }

    createMutation.mutate({
      name: formName.trim(),
      email: formEmail.trim().toLowerCase(),
      password: formPassword.trim(),
      role: formRole,
      organization: formOrg.trim() || undefined,
      publisherCode: formRole === 'penerbit' ? formPubCode.trim() || undefined : undefined,
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!editingUser) return;
    if (!formName.trim()) {
      setFormError('Nama pengguna tidak boleh kosong.');
      return;
    }

    const payload: any = {
      name: formName.trim(),
      role: formRole,
      organization: formOrg.trim() || undefined,
      publisherCode: (formRole === 'penerbit' || formRole === 'organisasi') ? formPubCode.trim() || undefined : undefined,
    };

    if (formRole === 'penerbit' || formRole === 'organisasi') {
      payload.publisherVerificationStatus = formVerificationStatus;
      payload.isPublisherVerified = formVerificationStatus === 'verified' ? 1 : 0;
      payload.publisherVerificationNotes = formVerificationNotes.trim() || undefined;
    }

    updateMutation.mutate({
      id: editingUser.id,
      payload,
    });
  };

  const handleDeleteConfirm = () => {
    if (!deletingUser) return;
    deleteMutation.mutate(deletingUser.id);
  };

  return (
    <div className="space-y-6">
      {/* Search and Action Bar */}
      <UserSearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isFetching={isFetching}
        onRefresh={() => refetch()}
        onOpenCreate={handleOpenCreate}
      />

      {/* Grid Counters */}
      <UserStatsGrid
        usersList={usersList}
        isLoading={isLoading}
        activeFilter={activeFilter}
        onSelectFilter={setActiveFilter}
      />

      {/* Pending Publisher Verification Queue Banner for Admin */}
      {pendingPublishersCount > 0 && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white shadow-md flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-in fade-in duration-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-white animate-pulse" />
            </div>
            <div>
              <p className="font-bold text-sm sm:text-base">
                Ada {pendingPublishersCount} Penerbit & Organisasi Menunggu Verifikasi
              </p>
              <p className="text-xs text-amber-100 mt-0.5">
                Penerbit dan organisasi baru tidak dapat mendaftarkan buku sebelum Anda memverifikasi berkas legalitas mereka.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
            <button
              type="button"
              onClick={() => {
                setActiveFilter('pending_publisher');
                const firstPending = usersList.find(
                  (u) => u.role === 'penerbit' && u.publisherVerificationStatus === 'pending'
                );
                if (firstPending) {
                  handleOpenPublisherVerification(firstPending);
                }
              }}
              className="flex-1 sm:flex-none px-4 py-2 bg-white text-amber-900 hover:bg-amber-50 rounded-xl text-xs font-bold shadow-xs transition cursor-pointer"
            >
              Tinjau Sekarang
            </button>
            <button
              type="button"
              onClick={() => setActiveFilter(activeFilter === 'pending_publisher' ? 'all' : 'pending_publisher')}
              className="flex-1 sm:flex-none px-3.5 py-2 bg-amber-700/60 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition cursor-pointer"
            >
              {activeFilter === 'pending_publisher' ? 'Tampilkan Semua' : `Filter Antrean (${pendingPublishersCount})`}
            </button>
          </div>
        </div>
      )}

      {/* User Table/List & Pagination */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs overflow-hidden">
        <UserTable
          usersList={filteredUsers}
          isLoading={isLoading}
          currentUser={currentUser}
          onOpenEdit={handleOpenEdit}
          onOpenDelete={setDeletingUser}
          onOpenPublisherVerification={handleOpenPublisherVerification}
          onToggleVerifyPublisher={handleToggleVerifyPublisher}
        />

        <UserPagination
          page={page}
          limit={limit}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {/* CREATE USER MODAL */}
      <CreateUserModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreateSubmit}
        isPending={createMutation.isPending}
        formName={formName}
        setFormName={setFormName}
        formEmail={formEmail}
        setFormEmail={setFormEmail}
        formPassword={formPassword}
        setFormPassword={setFormPassword}
        formRole={formRole}
        setFormRole={setFormRole}
        formOrg={formOrg}
        setFormOrg={setFormOrg}
        formPubCode={formPubCode}
        setFormPubCode={setFormPubCode}
        formError={formError}
      />

      {/* EDIT USER MODAL */}
      <EditUserModal
        editingUser={editingUser}
        onClose={() => setEditingUser(null)}
        onSubmit={handleEditSubmit}
        isPending={updateMutation.isPending}
        formName={formName}
        setFormName={setFormName}
        formEmail={formEmail}
        formRole={formRole}
        setFormRole={setFormRole}
        formOrg={formOrg}
        setFormOrg={setFormOrg}
        formPubCode={formPubCode}
        setFormPubCode={setFormPubCode}
        formVerificationStatus={formVerificationStatus}
        setFormVerificationStatus={setFormVerificationStatus}
        formVerificationNotes={formVerificationNotes}
        setFormVerificationNotes={setFormVerificationNotes}
        formError={formError}
      />

      {/* ADMIN PUBLISHER VERIFICATION MODAL */}
      <PublisherVerificationModalAdmin
        user={verifyingPublisher}
        isOpen={Boolean(verifyingPublisher)}
        onClose={() => setVerifyingPublisher(null)}
        onSubmit={handlePublisherVerificationSubmit}
        isPending={verifyPublisherMutation.isPending}
      />

      {/* DELETE USER CONFIRMATION MODAL */}
      <DeleteUserModal
        deletingUser={deletingUser}
        onClose={() => setDeletingUser(null)}
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};
